import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Inject,
  OnInit,
  Output,
  OnChanges,
  SimpleChanges,
  ViewChild,
  QueryList,
  ViewChildren,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { DOCUMENT } from '@angular/common';
import * as R from 'ramda';
import {
  config,
  getCountryConfig,
  getCountryPostalCodePattern,
} from '../config/country-config';
import {
  registrationData,
  FieldsConfig,
  ErrorMsgs,
  FieldConfig,
  FormControlTypes,
  FieldNames,
  postalCodeTranslatedLang,
  nameFieldRegex,
  fncValidateNoSpaces,
} from './registration.model';
import {
  DOBValidator,
  calculateAge,
} from '../validators/date-of-birth.validators';
import { Subscription } from 'rxjs/internal/Subscription';
import { RegistrationLabels, StatesObj } from '../app.models';
import { isStatesAllowed } from '../config/app-config';
import { Store } from '@ngrx/store';
import { GetStates } from '../store/states/states.actions';
import { stateInfo } from '../store/states';
import { StringUtils } from '../shared/utils/string-utils';
import { SenderRegistrationInfo } from '../store/transaction/types';
import { combineLatest, of } from 'rxjs';
import { CountryObj } from '../store/types';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import {
  postcodeValidator,
  postcodeValidatorExistsForCountry,
} from 'postcode-validator';
import { FormSearchUtils } from '../shared/utils/form-search-utils';

@Component({
    selector: 'app-registration',
    templateUrl: './registration.component.html',
    styleUrls: ['./registration.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class RegistrationComponent implements OnInit, OnChanges {
  phoneCountryCode: string = '';
  registrationLabels!: RegistrationLabels;
  dateOfBirthComponent!: any;
  isNewUser: boolean = false;
  reviewStatus: string;
  initialCountrySelected: boolean = false;
  formSearchUtils = new FormSearchUtils();
  public _google: any;
  @Input() public set google(map: any) {
    this._google = map;
    if (this._google) {
      // this.initAutocomplete();
    }
  }
  public get google() {
    return this._google;
  }

  @Input() isStateFieldError: any;
  @Input() locale: string;
  @Input() isCountryFieldError: any;

  private _labels: any;
  @Input() public set labels(labels: any) {
    this._labels = labels;
    this.registrationLabels = this.labels.stellar.registration;
    this.dateOfBirthComponent =
      this.labels.stellar.registration?.dateOfBirthComponent;
    if (this.labels.stellar.registration) {
      const addressFields =
        registrationData &&
        registrationData?.fields &&
        registrationData?.fields?.addressInfoFields &&
        registrationData?.fields?.addressInfoFields?.filter((field) => {
          field.fieldLabel = labels.stellar.registration[field.name];
          return field;
        });
      const personalFields =
        registrationData &&
        registrationData?.fields &&
        registrationData?.fields?.personalInfoFields &&
        registrationData?.fields?.personalInfoFields?.filter((field) => {
          field.fieldLabel = labels.stellar.registration[field.name];
          return field;
        });
      registrationData.fields.addressInfoFields = addressFields;
      registrationData.fields.personalInfoFields = personalFields;
    }
  }
  public get labels() {
    return this._labels;
  }

  private _transactionCountryCode?: string;
  @Input() public set transactionCountryCode(code: string) {
    this._transactionCountryCode = code;
  }

  public get transactionCountryCode() {
    return this._transactionCountryCode!;
  }

  private _senderData?: SenderRegistrationInfo;
  @Input() public set senderData(data: SenderRegistrationInfo) {
    this._senderData = data;
    const isExistingUser =
      !R.isEmpty(data) &&
      data?.firstName &&
      data?.lastName &&
      !R.isEmpty(data.address) &&
      data.address?.line1
        ? true
        : false;
    this.isNewUser = !isExistingUser;
    if (isExistingUser) {
      this.setDefaultValues();
    }
  }

  public get senderData(): SenderRegistrationInfo {
    return this._senderData!;
  }

  private _countries?: any;
  @Input() public set countries(countries: any) {
    this._countries = countries;
  }

  public get countries(): any {
    return this._countries;
  }

  private _endpoint: string;
  @Input() public set endpoint(endpointValue: string) {
    this._endpoint = endpointValue;
  }
  public get endpoint() {
    return this._endpoint;
  }

  public form!: FormGroup;
  public dateOfBirth!: FormGroup;
  public registrationData = registrationData;
  public fields: FieldsConfig = {};
  private fieldsTemplate: any;
  public personalFields!: FieldConfig[];
  public addressFields!: FieldConfig[];
  public filteredCountries: any = [];
  public filteredStates: any = [];
  public countryStates: any[] = [];
  public errorMsgToDisplay: string = 'field has error';
  public ErrorMsgs = ErrorMsgs;
  public dateFrom: Date = new Date();
  public readonly controlTypes: typeof FormControlTypes = FormControlTypes;
  public readonly countryConfig = config;
  public readonly formFields: typeof FieldNames = FieldNames;
  public mobileNumberConfig!: FieldConfig;
  public mobileCountryConfig!: FieldConfig;
  public defaultCountryCode!: string;
  public phoneData!: any;
  public autocomplete: any;
  public stateOptions: any = [];
  public country!: any;

  isDOBValid: boolean = false;
  isDOBError: boolean = false;
  isConfirmed: boolean = false;
  personalDetailsAreValid: boolean = false;
  countryArrowIcon: string = 'keyboard_arrow_down';
  stateArrowIcon: string = 'keyboard_arrow_down';
  @ViewChildren(MatAutocompleteTrigger)
  triggers: QueryList<MatAutocompleteTrigger>;

  private _watches: Subscription[] = [];

  @Output() public submitted: EventEmitter<any> = new EventEmitter<any>();
  constructor(
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private store: Store<any>,
    @Inject(DOCUMENT) private document: any
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes?.isStateFieldError?.currentValue && this.form) {
      this.form.get('state')?.setValue(null);
    }
    if (changes?.isCountryFieldError?.currentValue && this.form) {
      this.form.get('country')?.setValue(null);
    }
  }

  ngOnInit(): void {
    this.form = this.fb.group({
      dateOfBirth: [this.dateOfBirth, Validators.required],
    });
    document.addEventListener('onautocomplete', function (e) {
      e.preventDefault(); // prevent autocomplete
    });
    this.fieldsTemplate =
      registrationData &&
      registrationData.personalFieldsTemplate &&
      registrationData.addressFieldsTemplate?.concat(
        registrationData.personalFieldsTemplate
      );
    this.personalFields = registrationData.fields?.personalInfoFields!;
    this.addressFields = registrationData.fields?.addressInfoFields!;
    this.filteredCountries = of(this._countries);

    this.store.select(stateInfo).subscribe((states: any) => {
      this.stateOptions = states && states?.states ? states.states : [];
      this.filteredStates = of(this.stateOptions);
      if (this.form.get('state')?.value) {
        this.form.get('state')?.setValue(this.form.get('state')?.value);
        this.form.get('state')?.updateValueAndValidity();
      }
    });

    this.form.valueChanges.subscribe((values: any) => {
      if (
        values?.firstName &&
        values?.lastName &&
        values?.firstName?.match(nameFieldRegex) &&
        values?.lastName?.match(nameFieldRegex) &&
        this.isDOBError
      ) {
        this.personalDetailsAreValid = true;
        this.reviewStatus = this.registrationLabels.review;
      }

      if (values?.personalInfoConfirmation) {
        this.isConfirmed = true;
        this.reviewStatus = this.registrationLabels.confirmed;
      } else if (!values?.personalInfoConfirmation) {
        this.isConfirmed = false;
      }
    });

    this.form.get('dateOfBirth')?.valueChanges.subscribe((date: any) => {
      if (date == 'Invalid Date') {
        this.isDOBError = false;
        this.form.get('dateOfBirth')?.setErrors({ invalidOption: true });
      } else if (date) {
        const dateObj = new Date(date);
        const month = ('0' + (dateObj.getMonth() + 1)).slice(-2);
        const day = ('0' + dateObj.getDate()).slice(-2);
        const dateString = dateObj.getFullYear() + '-' + month + '-' + day;
        const age = calculateAge(dateString || '');
        if (age < 18) {
          this.isDOBError = false;
          this.form.get('dateOfBirth')?.setErrors({ min: true });
        } else if (age >= 100) {
          this.isDOBError = false;
          this.form.get('dateOfBirth')?.setErrors({ max: true });
        } else {
          this.isDOBError = true;
          this.form.get('dateOfBirth')?.setErrors(null);
        }
      }
    });

    // adding personal Info confirmation checkbox for new users
    if (this.isNewUser) {
      this.form?.addControl(
        'personalInfoConfirmation',
        this.fb.control(false, { validators: [Validators.requiredTrue] })
      );
    }

    this.addFieldValidators(registrationData.fields!);

    this._watches.push(
      this.form.controls['country'].valueChanges.subscribe((value: any) => {
        let filterValue = value
          ? StringUtils.removeDiacriticsAndConvertToLowercase(value)
          : '';
        let filteredOptions: any = this._countries?.filter((country: any) => {
          return (
            (country.name &&
              StringUtils.removeDiacriticsAndConvertToLowercase(
                country.name
              ).startsWith(filterValue)) ||
            (country.name &&
              StringUtils.removeDiacriticsAndConvertToLowercase(
                country.code
              ).startsWith(filterValue))
          );
        });
        this.handleFilteredCountryOption(
          filterValue,
          FieldNames.country,
          filteredOptions
        );
        filteredOptions = filteredOptions ? filteredOptions : [];
        this.filteredCountries = of(filteredOptions);
      })
    );

    this._watches.push(
      this.form.controls['state'].valueChanges.subscribe((value: any) => {
        const filterValue = value
          ? StringUtils.removeDiacriticsAndConvertToLowercase(value)
          : '';
        let filteredOptions: StatesObj[] = this.stateOptions?.filter(
          (state: StatesObj) => {
            return (
              (state.name &&
                StringUtils.removeDiacriticsAndConvertToLowercase(
                  state.name
                ).startsWith(filterValue)) ||
              (state.code &&
                StringUtils.removeDiacriticsAndConvertToLowercase(
                  state.code
                ).startsWith(filterValue))
            );
          }
        );
        const errorKey: string[] = ['required', 'stateNotFoundError'];
        this.formSearchUtils.handleFilteredStateOption(
          filterValue,
          FieldNames.state,
          filteredOptions,
          this.form,
          this.triggers,
          errorKey
        );
        filteredOptions = filteredOptions ? filteredOptions : [];
        this.filteredStates = of(filteredOptions);
      })
    );

    // Run validation on the postal code based on the selected country
    this._watches.push(
      combineLatest([
        this.form.controls['postalCode'].valueChanges,
        this.form.controls['country'].valueChanges,
      ]).subscribe((value: any[]) => {
        const countryCode = this.form.get('country')?.value || '';
        this.validatePostcode(value[0], countryCode);
      })
    );

    // Uncheck/reset personalInfoConfirmation checkbox if the uses changes the input for firstName, lastName, of dateOfBirth
    this._watches.push(
      combineLatest([
        this.form.controls['firstName'].valueChanges,
        this.form.controls['lastName'].valueChanges,
        this.form.controls['dateOfBirth'].valueChanges,
      ]).subscribe((value: any[]) => {
        if (this.form.get('personalInfoConfirmation')?.value) {
          this.form.controls['personalInfoConfirmation'].setValue(false);
        }

        if (
          !this.form.get('firstName')?.value ||
          !this.form.get('lastName')?.value
        ) {
          this.form.controls['personalInfoConfirmation'].setValue(false);
          this.personalDetailsAreValid = false;
          this.reviewStatus = '';
        }
      })
    );
  }

  private addFieldValidators(fields: FieldsConfig) {
    const allFields = fields.addressInfoFields?.concat(
      fields.personalInfoFields!
    );
    this.fieldsTemplate.forEach((fieldName: any) => {
      const fieldConfig = allFields?.find(
        (fieldConfig: FieldConfig) => fieldConfig.name === fieldName
      );

      if (
        fieldName === this.formFields.mobileNumber ||
        fieldName === this.formFields.mobileCountry
      ) {
        if (fieldName === FieldNames.mobileNumber && fieldConfig) {
          this.mobileNumberConfig = fieldConfig;
        }
        if (fieldName === FieldNames.mobileCountry && fieldConfig) {
          this.mobileCountryConfig = fieldConfig;
        }
      } else {
        if ((fieldName === this.formFields.state, fieldConfig)) {
          // this.setStates(this.transactionCountryCode);
        }

        const validators: ValidatorFn[] = [];
        if (
          [this.formFields.address, this.formFields.city].includes(fieldName)
        ) {
          validators.push(fncValidateNoSpaces);
        }
        if (fieldConfig?.required) {
          validators.push(Validators.required);
        }

        if (fieldConfig?.pattern && fieldName !== FieldNames.dob) {
          validators.push(Validators.pattern(fieldConfig!.pattern));
        }

        if (fieldConfig?.pattern && fieldName === FieldNames.dob) {
          validators.push(DOBValidator());
        }

        if (fieldConfig?.fieldMaxLength) {
          validators.push(Validators.maxLength(fieldConfig!.fieldMaxLength));
        }

        if (fieldConfig?.fieldMinLength) {
          validators.push(Validators.minLength(fieldConfig!.fieldMinLength));
        }

        this.form.addControl(fieldName, this.fb.control('', { validators }));
      }

      if (fieldConfig?.disabled) {
        this.form.get(fieldName)?.disable();
      }
    });
  }

  private setStates(code: string) {
    const stateIndex = this.addressFields?.findIndex(
      (field) => field.name === FieldNames.state
    );
    const country = this.countryConfig.find((country) => country.code === code);
    if (country && country?.states && stateIndex) {
      this.countryStates = country.states;
      this.addressFields[stateIndex] = {
        ...this.addressFields[stateIndex],
        options: this.countryStates,
      };
    }
  }

  public getErrorsMsg(fieldName: any, fieldConfig: any): any {
    if (fieldConfig.invalid && (fieldConfig.touched || fieldConfig.dirty)) {
      if (fieldConfig.hasError('required')) {
        return (this.errorMsgToDisplay =
          fieldName + ' ' + this.labels?.stellar?.error?.isRequired);
      } else if (fieldConfig.hasError('maxlength')) {
        return (this.errorMsgToDisplay =
          fieldName ===
          Object.keys(postalCodeTranslatedLang).find(
            (transPostalCodeVal) => transPostalCodeVal === fieldName
          )
            ? fieldName +
              ' ' +
              this.labels?.stellar?.registration?.errorMsgs?.maxLength +
              '. ' +
              this.labels?.stellar?.registration?.errorMsgs?.invalidPostalCode +
              '.'
            : fieldName +
              ' ' +
              this.labels?.stellar?.registration?.errorMsgs?.maxLength +
              '. ');
      } else if (fieldConfig.hasError('minlength')) {
        return (this.errorMsgToDisplay =
          fieldName ===
          Object.keys(postalCodeTranslatedLang).find(
            (transPostalCodeVal) => transPostalCodeVal === fieldName
          )
            ? fieldName +
              ' ' +
              this.labels?.stellar?.registration?.errorMsgs?.minLength +
              '. ' +
              this.labels?.stellar?.registration.errorMsgs?.invalidPostalCode +
              '.'
            : fieldName +
              ' ' +
              this.labels?.stellar?.registration?.errorMsgs?.minLength +
              '. ');
      } else if (fieldConfig.hasError('max')) {
        return (this.errorMsgToDisplay =
          fieldName +
          ' ' +
          this.labels?.stellar?.registration?.errorMsgs?.maxLength);
      } else if (fieldConfig.hasError('min')) {
        return (this.errorMsgToDisplay =
          fieldName +
          ' ' +
          this.labels?.stellar?.registration?.errorMsgs?.minLength);
      } else if (fieldConfig.hasError('invalidPostalCodeError')) {
        return (this.errorMsgToDisplay =
          this.labels?.stellar?.registration.errorMsgs?.invalidPostalCode);
      } else if (fieldConfig.hasError('invalidOption')) {
        return (this.errorMsgToDisplay = this.labels?.stellar?.optionNotFound);
      } else if (fieldConfig.hasError('stateNotFoundError')) {
        return (this.errorMsgToDisplay = this.labels?.stellar?.stateNotFound);
      } else if (fieldConfig.hasError('countryNotFoundError')) {
        return (this.errorMsgToDisplay = this.labels?.stellar?.countryNotFound);
      } else {
        if (this.form.controls?.addressLine2.value === fieldConfig.value) {
          return (this.errorMsgToDisplay =
            this.labels?.stellar?.registration?.errorMsgs?.onlyAlphanumeric);
        }
        return (this.errorMsgToDisplay =
          fieldName +
          ' ' +
          this.labels?.stellar?.registration?.errorMsgs?.invalid);
      }
    }
  }

  private setDefaultValues() {
    !!this.personalFields &&
      this.personalFields.forEach((field: any) => {
        if (
          this.senderData[field.name] &&
          field.name !== 'dateOfBirth' &&
          this.senderData[field.name].match(field.pattern)
        ) {
          const data = field.fieldMaxLength
            ? this.senderData[field.name].substring(0, field.fieldMaxLength)
            : this.senderData[field.name];
          this.form.controls[field.name].setValue(data);
          this.form.controls[field.name].disable();
        } else if (
          this.senderData[field.name] &&
          field.name === 'dateOfBirth'
        ) {
          const age = calculateAge(this.senderData.dateOfBirth || '');
          this.form.controls[field.name].setValue(this.senderData[field.name]);
          this.isDOBValid = age >= 18 && age < 100;
        }
      });

    if (this._senderData?.address) {
      const keys = Object.keys(this._senderData?.address!);
      const values = Object.values(this._senderData.address);

      if (keys.length > 0) {
        for (let i = 0; i < keys.length; i++) {
          if (this.form.controls[keys[i]]) {
            this.form.controls[keys[i]].setValue(values[i]);
            this.form.controls[keys[i]].markAsTouched();
          } else {
            switch (keys[i]) {
              case FieldNames.line1:
                this.form.controls[FieldNames.address].setValue(values[i]);
                this.form.controls[FieldNames.address].markAsTouched();
                break;
              case FieldNames.line2:
                this.form.controls[FieldNames.addressLine2].setValue(values[i]);
                this.form.controls[FieldNames.addressLine2].markAsTouched();
                break;
              case FieldNames.countrySubdivisionCode:
                this.form.controls[FieldNames.state].setValue(values[i]);
                this.form.controls[FieldNames.state].markAsTouched();
                break;
              case FieldNames.countryCode:
                this.transactionCountryCode =
                  this._senderData.address[FieldNames.countryCode]!;
                this.country = this.transactionCountryCode;
                if (this.form.controls[FieldNames.country]) {
                  const country = this.countries.find(
                    (country: CountryObj) =>
                      country.code.toLowerCase() ===
                      this.transactionCountryCode.toLocaleLowerCase()
                  );
                  this.form.controls[FieldNames.country].setValue(country.name);
                  this.onCountryChange(country.code);
                }
                break;
              default:
                break;
            }
          }
        }
      }
    }

    if (this._senderData?.mobilePhone) {
      this.phoneData = {
        ...this._senderData.mobilePhone,
        country: this.country,
      };
    }

    // remove personalInfoConfirmation checkbox if not a new user
    if (!this.isNewUser && this.form.contains('personalInfoConfirmation')) {
      this.form.removeControl('personalInfoConfirmation');
    }

    this.cdr.detectChanges();
  }

  private initAutocomplete() {
    const inputElement = this.document.getElementById('address');
    const options = {
      fields: ['formatted_address', 'address_components'],
      types: ['address'],
      componentRestrictions: { country: this.transactionCountryCode },
    };
    const google = window['google'];
    if (google) {
      if (inputElement) {
        this.autocomplete = new google.maps.places.Autocomplete(
          inputElement,
          options
        );
        this.autocomplete.addListener('place_changed', () => {
          this.fillInAddress(this.autocomplete.getPlace());
        });
      }
    }
  }

  private fillInAddress(address: any) {
    const addressElements = address?.formatted_address.split(',');
    const addressComponents = address?.address_components;
    if (addressElements.length) {
      this.clearFields();
      for (let i = 0; i < addressElements.length; i++) {
        switch (i) {
          case 0:
            const addressLine1 = addressElements[i].trim();
            this.form.controls[this.formFields.address].setValue(addressLine1);
            break;
          case 1:
            const city = addressElements[i].trim();
            this.form.controls[this.formFields.city].setValue(city);
            break;
          case 2:
            const state =
              this.transactionCountryCode +
              '-' +
              addressElements[i].trim().split(' ')[0];
            this.form.controls[this.formFields.state].setValue(state);
            const postalCode = addressElements[i].trim().split(' ')[1];
            this.form.controls[this.formFields.postalCode].setValue(postalCode);
            break;
          case 3:
            const country = addressElements[i].trim();
            this.form.controls[this.formFields.country].setValue(country);
            break;
        }
      }
    }
    this.cdr.detectChanges();
  }

  private clearFields() {
    this.form.controls[this.formFields.address].reset();
    this.form.controls[this.formFields.addressLine2].reset();
    this.form.controls[this.formFields.city].reset();
    this.form.controls[this.formFields.country].reset();
    this.form.controls[this.formFields.state].reset();
  }

  public getSelectedOptionLabel(options: any, key: string, fieldValue: any) {
    if (!fieldValue) {
      return '';
    }
    const index = options?.findIndex(
      (option: any) => option[key] === fieldValue
    );
    return options[index]?.name;
  }

  public onCountryChange(selectedCountry: any) {
    this.country = selectedCountry;
    if (isStatesAllowed(this.country)) {
      this.form.controls['state'].enable();
      const payload = {
        country: this.country,
        userLanguage: 'en-US',
      };
      if (this._senderData?.address?.countryCode !== this.country) {
        this.form.controls['state'].setValue('');
      }
      this.store.dispatch(
        GetStates({ getStatesRequest: payload, endpoint: this.endpoint })
      );
      this.cdr.detectChanges();
    } else {
      this.form.controls['state'].setValue('');
      this.form.controls['state'].disable();
    }
  }

  private handleFilteredCountryOption(
    filterValue: string,
    controlName: string,
    countries: CountryObj[]
  ) {
    const formControl = this.form.get(controlName);
    filterValue =
      StringUtils.removeDiacriticsAndConvertToLowercase(filterValue);
    if (countries.length === 1) {
      const option = countries[0];
      const isMatch =
        filterValue ===
        StringUtils.removeDiacriticsAndConvertToLowercase(option.name);
      if (isMatch) {
        formControl?.setValue(option.code, { emitEvent: false });
        this.onCountryChange(option.code);
        this.closePanel();
        this.clearStateErrorField();
      } else {
        this.handleCountryNoMatch(filterValue, countries, formControl);
      }
    } else if (countries.length > 1) {
      const matchingCountry = countries.filter(
        (country: CountryObj) =>
          filterValue ===
          StringUtils.removeDiacriticsAndConvertToLowercase(country.name)
      );
      if (matchingCountry.length === 1) {
        const option = matchingCountry[0];
        formControl?.setValue(option.code, { emitEvent: false });
        this.onCountryChange(option.code);
        this.closePanel();
        this.clearStateErrorField();
      } else {
        this.handleCountryNoMatch(filterValue, countries, formControl);
      }
    } else {
      this.handleCountryNoMatch(filterValue, countries, formControl);
    }
  }

  clearStateErrorField() {
    const stateControl = this.form.get(FieldNames.state);
    if (!this.initialCountrySelected) {
      stateControl?.markAsUntouched();
      stateControl?.markAsPristine();
    }
    if (stateControl?.value) {
      stateControl.updateValueAndValidity();
    }
    this.initialCountrySelected = true;
  }

  handleCountryNoMatch(
    filterValue: string,
    countries: CountryObj[],
    formControl: AbstractControl<any | any> | null
  ): void {
    if (!filterValue && countries.length) {
      this.setCountryFormError(formControl, 'required');
    } else {
      this.setCountryFormError(formControl, 'countryNotFoundError');
    }
  }

  setCountryFormError(
    formControl: AbstractControl<any | any> | null,
    errorType: string
  ): void {
    formControl?.setErrors({ [errorType]: true });
  }

  getFilteredCountries(value: any) {
    const filterValue = value
      ? StringUtils.removeDiacritics(value.toLowerCase())
      : '';
    let filteredOptions: any = this._countries?.filter((country: any) => {
      return (
        country.name &&
        StringUtils.removeDiacritics(country.name.toLowerCase()).startsWith(
          filterValue
        )
      );
    });
    if (
      filterValue &&
      !!getCountryConfig(filterValue)?.code &&
      filteredOptions.length !== 0
    ) {
      filteredOptions = filteredOptions.filter(
        (country: any) => country.code.toLowerCase() === filterValue
      );
    }
    if (filterValue && filteredOptions.length === 0) {
      filteredOptions = this._countries?.filter(
        (country: any) => country.code.toLowerCase() === filterValue
      );
    }
    filteredOptions = filteredOptions ? filteredOptions : [];
    return filteredOptions;
  }

  getFilteredStates(value: any) {
    const filterValue = value
      ? StringUtils.removeDiacritics(value.toLowerCase())
      : '';
    let filteredOptions: any = this.stateOptions?.filter((state: any) => {
      return (
        state.name &&
        StringUtils.removeDiacritics(state.name.toLowerCase()).startsWith(
          filterValue
        )
      );
    });
    if (filterValue && filteredOptions.length === 0) {
      filteredOptions = this.stateOptions?.filter(
        (state: any) => state.code.toLowerCase() === filterValue
      );
    }
    filteredOptions = filteredOptions ? filteredOptions : [];
    return filteredOptions;
  }

  onAutoCompleteCountryPanelToggle(isOpen: boolean) {
    if (isOpen) {
      this.countryArrowIcon = 'keyboard_arrow_up';
    } else {
      this.countryArrowIcon = 'keyboard_arrow_down';
    }
  }
  onAutoCompleteStatePanelToggle(isOpen: boolean) {
    if (isOpen) {
      this.stateArrowIcon = 'keyboard_arrow_up';
    } else {
      this.stateArrowIcon = 'keyboard_arrow_down';
    }
  }

  onDropdownPanelToggle(
    isOpen: any,
    dropdownElement: any,
    countrySearchInput: any
  ) {
    if (isOpen) {
      this.countryArrowIcon = 'keyboard_arrow_up';
      dropdownElement.panel.nativeElement.scrollTop = 0;
    } else {
      this.countryArrowIcon = 'keyboard_arrow_down';
      countrySearchInput.value = '';
      this.filteredCountries = [...this._countries];
    }
  }

  private validatePostcode(postcode: string, countryCode: string): void {
    let isPostcodeValid = false;
    // convert countryCode to iso2
    const iso2code = getCountryConfig(countryCode)?.iso2Code;

    if (postcode && iso2code && postcodeValidatorExistsForCountry(iso2code)) {
      isPostcodeValid = postcodeValidator(postcode, iso2code);
    }
    // If lib does not support and country has specific pattern
    else if (
      postcode &&
      iso2code &&
      !postcodeValidatorExistsForCountry(iso2code)
    ) {
      const pattern = getCountryPostalCodePattern(iso2code);
      isPostcodeValid = !!pattern
        ? !!postcode.match(pattern)
        : !!postcode.match('^[a-zA-Z0-9-]*$');
    } else {
      isPostcodeValid = !!postcode.match('^[a-zA-Z0-9-]*$');
    }

    if (postcode && postcode.length < 3) {
      this.form.controls['postalCode'].setErrors({ min: true });
    } else if (!isPostcodeValid) {
      this.form.controls['postalCode'].setErrors({
        invalidPostalCodeError: true,
      });
    } else if (!postcode || postcode.length < 1) {
      this.form.controls['postalCode'].setErrors({ required: true });
    } else {
      this.form.controls['postalCode'].setErrors(null);
    }
  }
  private closePanel() {
    this.triggers.forEach((trigger: MatAutocompleteTrigger) => {
      if (trigger.panelOpen) {
        setTimeout(() => trigger.closePanel());
      }
    });
  }
}
